import { apiClient } from '../httpClient';
import { handleApiError, isDuplicateError } from '../utils/errorHandler';

export const infoService = {
  // 회사 목록 조회
  getCompanies: async () => {
    try {
      const response = await apiClient.get('/info/v2/vendor/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '회사 목록을 가져오는데 실패했습니다.');
    }
  },

  // 회사 상세 조회
  getCompany: async (id) => {
    try {
      const response = await apiClient.get(`/info/v2/vendor/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '회사 정보를 가져오는데 실패했습니다.');
    }
  },

  // 회사 생성
  createCompany: async (companyData) => {
    try {
      const requestData = {
        partner_name: companyData.partner_name?.trim(),
        biz_reg_no: companyData.biz_reg_no || null,
        boss_name: companyData.boss_name || null,
        biz_cat1: companyData.biz_cat1 || null,
        biz_cat2: companyData.biz_cat2 || null,
        postcode: companyData.postcode ? String(companyData.postcode).trim() : null,
        address1: companyData.address1 || null,
        address2: companyData.address2 || null,
        partner_group: null,
        specification: null,
        partner_type: 'VENDOR',
        extra_info: companyData.extra_info || null
      };

      Object.keys(requestData).forEach(key =>
        requestData[key] === undefined && delete requestData[key]
      );

      const response = await apiClient.post('/info/v2/vendor/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate'
          }
        };
      }
      return handleApiError(error, '회사 생성에 실패했습니다.');
    }
  },

  // 회사 수정
  updateCompany: async (id, companyData) => {
    try {
      const requestData = {
        partner_name: companyData.partner_name?.trim() || null,
        biz_reg_no: companyData.biz_reg_no?.trim() || null,
        boss_name: companyData.boss_name?.trim() || null,
        biz_cat1: companyData.biz_cat1?.trim() || null,
        biz_cat2: companyData.biz_cat2?.trim() || null,
        postcode: companyData.postcode ? String(companyData.postcode).trim() : null,
        address1: companyData.address1?.trim() || null,
        address2: companyData.address2?.trim() || null,
        partner_group: null,
        specification: null,
        partner_type: 'VENDOR',
        extra_info: companyData.extra_info ?? null
      };

      Object.keys(requestData).forEach(key =>
        requestData[key] === undefined && delete requestData[key]
      );

      const response = await apiClient.put(`/info/v2/vendor/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '회사 수정에 실패했습니다.');
    }
  },

  // 회사 삭제
  deleteCompany: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/vendor/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '회사 삭제에 실패했습니다.');
    }
  },

  // 구매자 목록 조회
  getBuyers: async () => {
    try {
      const response = await apiClient.get('/info/v2/buyer/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '구매자 목록을 가져오는데 실패했습니다.');
    }
  },

  // 구매자 생성
  createBuyer: async (buyerData) => {
    try {
      const requestData = {
        partner_type: 'BUYER',
        partner_group: buyerData.partner_group?.trim() || '',
        partner_name: buyerData.partner_name?.trim() || '-',
        specification: buyerData.specification?.trim() || '-',
        biz_reg_no: null,
        boss_name: null,
        biz_cat1: null,
        biz_cat2: null,
        postcode: null,
        address1: null,
        address2: null,
        partner_code: null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/buyer/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_category_buyer'
          }
        };
      }
      return handleApiError(error, '구매자 생성에 실패했습니다.');
    }
  },

  // 구매자 수정
  updateBuyer: async (id, buyerData) => {
    try {
      const requestData = {
        partner_type: 'BUYER',
        partner_group: buyerData.partner_group?.trim() || '',
        partner_name: buyerData.partner_name?.trim() || '-',
        specification: buyerData.specification?.trim() || '-',
        biz_reg_no: null,
        boss_name: null,
        biz_cat1: null,
        biz_cat2: null,
        postcode: null,
        address1: null,
        address2: null,
        partner_code: null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.put(`/info/v2/buyer/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_category_buyer'
          }
        };
      }
      return handleApiError(error, '구매자 수정에 실패했습니다.');
    }
  },

  // 구매자 삭제
  deleteBuyer: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/buyer/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '구매자 삭제에 실패했습니다.');
    }
  },

  // 공장 목록 조회
  getFactories: async () => {
    try {
      const response = await apiClient.get('/info/v3/delivery/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '공장 목록을 가져오는데 실패했습니다.');
    }
  },

  // 공장 생성
  createFactory: async (factoryData) => {
    try {
      const requestData = {
        partner_id: factoryData.partner_id,
        delivery_name: factoryData.delivery_name?.trim(),
        specification: factoryData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/delivery/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_factory'
          }
        };
      }
      return handleApiError(error, '공장 생성에 실패했습니다.');
    }
  },

  // 공장 수정
  updateFactory: async (id, factoryData) => {
    try {
      const requestData = {
        partner_id: factoryData.partner_id,
        delivery_name: factoryData.delivery_name?.trim(),
        specification: factoryData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.put(`/info/v2/delivery/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_factory'
          }
        };
      }
      return handleApiError(error, '공장 수정에 실패했습니다.');
    }
  },

  // 공장 삭제
  deleteFactory: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/delivery/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '공장 삭제에 실패했습니다.');
    }
  },

  // 담당자 목록 조회
  getManagers: async () => {
    try {
      const response = await apiClient.get('/info/v2/manager/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '담당자 목록을 가져오는데 실패했습니다.');
    }
  },

  // 담당자 생성
  createManager: async (managerData) => {
    try {
      const requestData = {
        partner_id: managerData.partner_id,
        manager_name: managerData.manager_name?.trim(),
        specification: managerData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/manager/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_manager'
          }
        };
      }
      return handleApiError(error, '담당자 생성에 실패했습니다.');
    }
  },

  // 담당자 수정
  updateManager: async (id, managerData) => {
    try {
      const requestData = {
        partner_id: managerData.partner_id,
        manager_name: managerData.manager_name?.trim(),
        specification: managerData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.put(`/info/v2/manager/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_manager'
          }
        };
      }
      return handleApiError(error, '담당자 수정에 실패했습니다.');
    }
  },

  // 담당자 삭제
  deleteManager: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/manager/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '담당자 삭제에 실패했습니다.');
    }
  },

  // 원사 목록 조회
  getThreads: async () => {
    try {
      const response = await apiClient.get('/info/v2/thread/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '원사 목록을 가져오는데 실패했습니다.');
    }
  },

  // 원사 생성
  createThread: async (threadData) => {
    try {
      const requestData = {
        thread_name: threadData.thread_name?.trim(),
        thread_spec: threadData.yarn_type?.trim(),
        specification: threadData.specification?.trim(),
        yarn_type: threadData.yarn_type?.trim(),
        weight: threadData.weight ? parseFloat(threadData.weight) : 0,
        unit: threadData.unit?.trim() || '',
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/thread/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_thread'
          }
        };
      }
      return handleApiError(error, '원사 생성에 실패했습니다.');
    }
  },

  // 원사 수정
  updateThread: async (id, threadData) => {
    try {
      const requestData = {
        thread_name: threadData.thread_name?.trim(),
        thread_spec: threadData.yarn_type?.trim(),
        specification: threadData.specification?.trim(),
        yarn_type: threadData.yarn_type?.trim(),
        weight: threadData.weight ? parseFloat(threadData.weight) : 0,
        unit: threadData.unit?.trim() || '',
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.put(`/info/v2/thread/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_thread'
          }
        };
      }
      return handleApiError(error, '원사 수정에 실패했습니다.');
    }
  },

  // 원사 삭제
  deleteThread: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/thread/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '원사 삭제에 실패했습니다.');
    }
  },

  // 사이즈 목록 조회
  getSizes: async () => {
    try {
      const response = await apiClient.get('/info/v2/size/list');
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '사이즈 목록을 가져오는데 실패했습니다.');
    }
  },

  // 사이즈 생성
  createSize: async (sizeData) => {
    try {
      const requestData = {
        size_name: sizeData.size_name?.trim(),
        specification: sizeData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/size/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_size'
          }
        };
      }
      return handleApiError(error, '사이즈 생성에 실패했습니다.');
    }
  },

  // 사이즈 수정
  updateSize: async (id, sizeData) => {
    try {
      const requestData = {
        size_name: sizeData.size_name?.trim(),
        specification: sizeData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.put(`/info/v2/size/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_size'
          }
        };
      }
      return handleApiError(error, '사이즈 수정에 실패했습니다.');
    }
  },

  // 사이즈 삭제
  deleteSize: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v2/size/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '사이즈 삭제에 실패했습니다.');
    }
  },

  // 가격 목록 조회
  getPrices: async (params = {}) => {
    try {
      const response = await apiClient.get('/info/v3/price/list', {
        params: {
          ...params
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '가격 목록을 가져오는데 실패했습니다.');
    }
  },

  // 가격 생성
  createPrice: async (requestData) => {
    try {
      console.log('311 create priceData', requestData);
      // const requestData = {
      //   partner_id: priceData.partner_id,
      //   thread_id: priceData.thread_id,
      //   price: parseFloat(priceData.price) || 0,
      //   price_currency: priceData.price_currency || 'USD',
      //   year: priceData.year || new Date().getFullYear()
      // };

      const response = await apiClient.post('/info/v3/price/', requestData);
      console.log('311 response', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_price'
          }
        };
      }
      return handleApiError(error, '가격 생성에 실패했습니다.');
    }
  },

  // 가격 수정
  updatePrice: async (id, requestData) => {
    try {
      const response = await apiClient.put(`/info/v3/price/${id}`, requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_price'
          }
        };
      }
      return handleApiError(error, '가격 수정에 실패했습니다.');
    }
  },

  // 가격 삭제
  deletePrice: async (id) => {
    try {
      const response = await apiClient.delete(`/info/v3/price/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '가격 삭제에 실패했습니다.');
    }
  },

  // 중량 목록 조회
  getWeights: async (params = {}) => {
    try {
      const response = await apiClient.get('/info/v2/weight/list', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '중량 목록을 가져오는데 실패했습니다.');
    }
  },

  // 중량 생성
  createWeight: async (weightData) => {
    try {
      const requestData = {
        thread_id: weightData.thread_id,
        weight: parseFloat(weightData.weight) || 0,
        unit: weightData.unit?.trim() || '',
        specification: weightData.specification?.trim() || null,
        creator_region: localStorage.getItem('user_region')?.toLowerCase() || 'korea'
      };

      const response = await apiClient.post('/info/v2/weight/', requestData);
      return { success: true, data: response.data };
    } catch (error) {
      if (isDuplicateError(error)) {
        return {
          success: false,
          error: {
            code: 400,
            message: 'duplicate_weight'
          }
        };
      }
      return handleApiError(error, '중량 생성에 실패했습니다.');
    }
  }
};
